// RUSTIC / MOODY DARK THEME — near-black surfaces, low-contrast borders, a single rust accent used sparingly. Built on
// a 4/8px spacing scale + one type scale so everything snaps to a grid. Shared so the game HUD can adopt it. The vibe:
// dark, mysterious, precision-engineered (Vercel/Linear/Stripe-grade), NOT loud.

export const TH = {
  // surfaces — deliberately near-black; boxes are barely above the background (mysterious, deep)
  bg: '#090a0c', bg2: '#0c0d10', side: '#0a0b0e', panel: '#0e0f13', raised: '#13151a', hover: '#16191f', sunk: '#060709',
  line: '#191c22', lineSoft: '#121419',
  // text — muted, never pure white
  text: '#cbc5ba', dim: '#6f6a60', faint: '#403d36',
  // accent — one rust, used only for primary/active. semantic roles for meaning.
  rust: '#c9772e', rustHi: '#e0913f', rustLo: '#6e3c16', glow: 'rgba(201,119,46,0.16)',
  good: '#5f8c4a', bad: '#a8452f', warn: '#b4863a',
  // spacing scale (4px base): use these, never ad-hoc px
  s1: '4px', s2: '8px', s3: '12px', s4: '16px', s5: '24px', s6: '32px',
  // type scale
  f1: '11px', f2: '12px', f3: '13px', f4: '15px', f5: '18px', f6: '24px',
  r: '8px', rsm: '5px', rlg: '12px',
  // depth — recessed dark wells + a hair of top light
  soft: '0 1px 2px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.018)',
  inset: 'inset 0 1px 4px rgba(0,0,0,0.6)',
} as const;

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, css: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag); e.style.cssText = css; if (text != null) e.textContent = text; return e;
}

export function tBtn(label: string, onClick: () => void, kind: 'primary' | 'ghost' | 'danger' = 'ghost'): HTMLButtonElement {
  const bg = kind === 'primary' ? TH.rust : kind === 'danger' ? '#23120e' : TH.raised;
  const color = kind === 'primary' ? '#120b04' : kind === 'danger' ? '#d99a86' : TH.text;
  const border = kind === 'primary' ? TH.rustLo : TH.line;
  const b = el('button', `color:${color};background:${bg};border:1px solid ${border};border-radius:${TH.rsm};padding:7px 13px;cursor:pointer;font:600 ${TH.f3} system-ui;box-shadow:${TH.soft};white-space:nowrap;transition:filter .12s,background .12s`, label);
  b.addEventListener('mouseenter', () => { b.style.filter = 'brightness(1.18)'; });
  b.addEventListener('mouseleave', () => { b.style.filter = ''; });
  b.addEventListener('click', onClick);
  return b;
}

// TYPED number field (no spinner) — type freely; invalid → red border; min clamps on commit.
export function tNum(value: number, onChange: (v: number) => void, opts?: { min?: number; w?: number; title?: string }): HTMLInputElement {
  const i = el('input', `width:${opts?.w ?? 66}px;padding:6px 8px;background:${TH.sunk};border:1px solid ${TH.line};border-radius:${TH.rsm};color:${TH.rustHi};font:600 ${TH.f3} ui-monospace,monospace;text-align:right;box-shadow:${TH.inset}`);
  i.type = 'text'; i.inputMode = 'decimal'; i.value = String(value);
  if (opts?.title) i.title = opts.title;
  i.addEventListener('focus', () => { i.style.borderColor = TH.rustLo; });
  i.addEventListener('blur', () => { i.style.borderColor = TH.line; });
  i.addEventListener('input', () => {
    const n = parseFloat(i.value);
    if (isNaN(n)) { i.style.borderColor = TH.bad; return; }
    i.style.borderColor = TH.rustLo;
    onChange(opts?.min != null ? Math.max(opts.min, n) : n);
  });
  return i;
}

export function tBadge(text: string, color: string): HTMLElement {
  return el('span', `display:inline-block;padding:2px 8px;border-radius:20px;font:700 ${TH.f1} system-ui;letter-spacing:0.5px;color:${color};border:1px solid ${color}55;background:${color}14`, text);
}

// Dark-metal grid background for the research canvas.
export function gridBg(cell: number): string {
  const c = 'rgba(255,255,255,0.028)';
  return `background-color:${TH.sunk};background-image:linear-gradient(${c} 1px,transparent 1px),linear-gradient(90deg,${c} 1px,transparent 1px);background-size:${cell}px ${cell}px`;
}
